// BUILD-042 — Unified employee workspace. Read-only employment-term history list, shared by My
// HR's "Employment history" sub-tab and (via the same splitTermHistory shape) consistent with
// HR Administration's own term-history rendering. Self-service is READ-ONLY here — recording a
// new employment change remains an HR Administration action (createEmploymentChange), never
// something this sub-tab exposes to the Employee themselves.
import type { TermLike } from '../termHistory';
import { CardTitle } from './SectionHeading';
import { useDepartmentNames } from '../useDepartmentNames';

interface Describable extends TermLike {
  title?: string | null;
  departmentId?: string | null;
  managerEmployeeId?: string | null;
  classification?: string | null;
  reason?: string | null;
}

function describe(t: Describable, managerName: string | null, departmentName: string | null): string {
  const parts: string[] = [];
  if (t.title) parts.push(t.title);
  if (t.managerEmployeeId) parts.push(`manager: ${managerName ?? t.managerEmployeeId}`);
  // The department by NAME (a disabled one included). Never the stored id; until the names load, or if it cannot be read, the department is simply left out.
  if (departmentName) parts.push(`dept: ${departmentName}`);
  if (t.reason) parts.push(`reason: ${t.reason}`);
  return parts.join(' · ') || '—';
}

export function EmploymentHistoryList<T extends Describable>({
  current, upcoming, history, managerName,
}: { current: T | null; upcoming: T[]; history: T[]; managerName: string | null }) {
  const { nameOf } = useDepartmentNames();
  return (
    <div className="space-y-4">
      {upcoming.length > 0 && (
        <div className="bg-white rounded-lg border border-amber-200 p-5">
          <h2 className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-3">Upcoming Change{upcoming.length > 1 ? 's' : ''}</h2>
          {upcoming.map((t, i) => (
            <div key={`${t.effectiveDate}-${i}`} className="text-sm py-1">{t.effectiveDate}: {describe(t, managerName, nameOf(t.departmentId))}</div>
          ))}
        </div>
      )}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <CardTitle>Current</CardTitle>
        {current ? (
          <div className="text-sm font-bold text-gray-900">{current.effectiveDate}: {describe(current, managerName, nameOf(current.departmentId))}</div>
        ) : (
          <div className="text-sm text-gray-400">No current term on file.</div>
        )}
      </div>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <CardTitle>Term history</CardTitle>
        {history.length === 0 ? (
          <div className="text-sm text-gray-400">No prior terms.</div>
        ) : (
          history.map((t, i) => (
            <div key={`${t.effectiveDate}-${i}`} className="text-sm py-1 border-t border-gray-100 first:border-t-0">{t.effectiveDate}: {describe(t, managerName, nameOf(t.departmentId))}</div>
          ))
        )}
      </div>
    </div>
  );
}
